import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import Topbar from './Topbar';
import Sidebar from './Sidebar';
import ContractorTable from './ContractorTable';
import SummaryBar from './SummaryBar';
import AmendModal from './modals/AmendModal';
import TerminateModal from './modals/TerminateModal';
import RequestsModal from './modals/RequestsModal';
import AmendDetailsModal from "./modals/AmendDetailsModal";
import AllContractorsTable from "./AllContractorsTable";
import config from "../config";

function ContractorPortal() {
  const { instance, accounts } = useMsal();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amendTarget, setAmendTarget] = useState(null);
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [amendDetailsTarget, setAmendDetailsTarget] = useState(null);
  const [showRequests, setShowRequests] = useState(false);
  const [isCMPAdmin, setIsCMPAdmin] = useState(false);
  const [viewAs, setViewAs] = useState("");
  const [viewAsInput, setViewAsInput] = useState("");
  const [viewingAs, setViewingAs] = useState(null);
  const [pendingRequests, setPendingRequests] = useState({});
  const [activeView, setActiveView] = useState("myContractors");
  const [allContractors, setAllContractors] = useState([]);
  const [allContractorsLoading, setAllContractorsLoading] = useState(false);

  async function fetchContractors(viewAsEmail) {
    setLoading(true);
    setError(null);
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });

      const url = viewAsEmail
        ? `/api/getContractors?viewAs=${encodeURIComponent(viewAsEmail)}`
        : '/api/getContractors';

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      setContractors(data.contractors);
      setIsCMPAdmin(data.isCMPAdmin);
      setViewingAs(data.viewingAs);
    } catch (err) {
      console.error('Error fetching contractors:', err);
      setError('Unable to load contractors. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchAllContractors() {
    setAllContractorsLoading(true);
    try {
      const tokenResponse = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      const response = await fetch("/api/getContractors?all=true", {
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
      });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      setAllContractors(data.contractors);
    } catch (err) {
      console.error("Error fetching all contractors:", err);
    } finally {
      setAllContractorsLoading(false);
    }
  }

  async function fetchPendingRequests() {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      const managerEmail = accounts[0]?.username;
      const response = await fetch(`/api/getRequests?managerEmail=${encodeURIComponent(managerEmail)}`, {
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      const pending = {};
      data.forEach(r => {
        if (!r.resolved && r.contractorEmail && r.type) {
          if (!pending[r.contractorEmail]) pending[r.contractorEmail] = {};
          pending[r.contractorEmail][r.type] = r.ticketNumber;
        }
      });
      setPendingRequests(pending);
    } catch (err) {
      console.error('Error fetching pending requests:', err);
    }
  }

  function handleNavClick(view) {
    setActiveView(view);
    if (view === "myRequests") { setShowRequests(true); return; }
    if (view === "allContractors" && allContractors.length === 0) fetchAllContractors();
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchContractors(null);
    fetchPendingRequests();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleViewAs = () => {
    setViewAs(viewAsInput);
    fetchContractors(viewAsInput);
  };

  const handleClearViewAs = () => {
    setViewAs("");
    setViewAsInput("");
    fetchContractors(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f0ef' }}>
      <Topbar />
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 56px)' }}>
        <Sidebar
          urgentCount={contractors.filter(c => c.daysRemaining <= 7).length}
          isCMPAdmin={isCMPAdmin}
          activeView={activeView}
          onNavClick={handleNavClick}
        />
        <main style={{ flex: 1, padding: '28px 32px', overflow: 'auto' }}>

          {activeView === "myContractors" && (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div>
                  <h1 style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em', color: '#18073a', lineHeight: 1 }}>My contractors</h1>
                  <p style={{ fontSize: '12px', color: '#79749c', marginTop: '4px', fontWeight: 400 }}>
                    {loading ? 'Loading...' : `${contractors.length} active · sorted by soonest expiry`}
                  </p>
                  {viewAs && (
                    <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', background: '#faeeda', color: '#854F0B', padding: '3px 8px', borderRadius: '20px', fontWeight: 600 }}>
                        Viewing as {viewingAs}
                      </span>
                      <button onClick={handleClearViewAs} style={{ fontSize: '11px', color: '#79749c', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'Montserrat, sans-serif' }}>
                        Back to my view
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => window.open(`https://${config.freshserviceDomain}/support/catalog/items/545`, '_blank')}
                  style={{ background: '#18073a', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}
                >
                  Onboard contractor ↗
                </button>
              </div>

              {isCMPAdmin && (
                <div style={{ background: '#fff', border: '0.5px solid #e0e0df', borderRadius: '8px', padding: '14px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#18073a', flexShrink: 0 }}>View as:</span>
                  <input
                    type="text"
                    placeholder="manager@inspiredenergy.co.uk"
                    value={viewAsInput}
                    onChange={e => setViewAsInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleViewAs()}
                    style={{ flex: 1, padding: '7px 12px', border: '0.5px solid #e0e0df', borderRadius: '6px', fontSize: '12px', fontFamily: 'Montserrat, sans-serif', color: '#18073a' }}
                  />
                  <button onClick={handleViewAs} style={{ background: '#18073a', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
                    Load
                  </button>
                </div>
              )}

              {loading && (
                <div style={{ background: '#fff', borderRadius: '8px', padding: '40px', textAlign: 'center', color: '#79749c', fontSize: '13px', border: '0.5px solid #e0e0df' }}>
                  Loading contractors…
                </div>
              )}

              {error && (
                <div style={{ background: '#f5e8ea', borderLeft: '3px solid #82162d', borderRadius: '0 8px 8px 0', padding: '14px 18px', fontSize: '13px', color: '#82162d', marginBottom: '20px' }}>
                  {error}
                </div>
              )}

              {!loading && !error && (
                <>
                  <SummaryBar contractors={contractors} />
                  <ContractorTable
                    contractors={contractors}
                    onAmend={setAmendTarget}
                    onTerminate={setTerminateTarget}
                    onAmendDetails={setAmendDetailsTarget}
                    pendingRequests={pendingRequests}
                  />
                </>
              )}
            </>
          )}

          {activeView === "allContractors" && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em', color: '#18073a', lineHeight: 1 }}>All Contractors</h1>
                <p style={{ fontSize: '12px', color: '#79749c', marginTop: '4px' }}>
                  {allContractorsLoading ? 'Loading…' : `${allContractors.length} contractors across all managers`}
                </p>
              </div>
              {allContractorsLoading
                ? <div style={{ background: '#fff', borderRadius: '8px', padding: '40px', textAlign: 'center', color: '#79749c', fontSize: '13px', border: '0.5px solid #e0e0df' }}>Loading contractors…</div>
                : <AllContractorsTable contractors={allContractors} />
              }
            </div>
          )}

        </main>
      </div>
      {showRequests && <RequestsModal onClose={() => setShowRequests(false)} />}
      {amendTarget && <AmendModal contractor={amendTarget} managerEmail={accounts[0]?.username} onClose={() => setAmendTarget(null)} onSuccess={fetchPendingRequests} />}
      {amendDetailsTarget && <AmendDetailsModal contractor={amendDetailsTarget} managerEmail={accounts[0]?.username} onClose={() => setAmendDetailsTarget(null)} onSuccess={fetchPendingRequests} />}
      {terminateTarget && <TerminateModal contractor={terminateTarget} managerEmail={accounts[0]?.username} onClose={() => setTerminateTarget(null)} onSuccess={fetchPendingRequests} />}
    </div>
  );
}

export default ContractorPortal;